import { useCallback, useRef, useState } from 'react';
import { env } from '../../../config/env';
import { reportError } from '../utils/errors';

const MAX_WINDOW_MS = 10 * 60 * 1000; // 10 minutes, matching the old fixed 3s×200 budget
const INITIAL_DELAY_MS = 2000;
const MAX_DELAY_MS = 15000;
const BACKOFF_FACTOR = 1.4;

export type PollingOutcome =
  | { success: true; appointmentId: unknown }
  | { success: false; timedOut: boolean };

interface PollArgs {
  paymentId: string;
  dbPtr: string;
  authToken: string;
}

/**
 * Replaces the fixed 3-second × 200-attempt loop in the old IndiaPayment.js
 * — same ~10 minute budget, but with backoff instead of hammering the
 * endpoint at a constant rate, and it's shared by every gateway instead of
 * being India-only (2C2P had no equivalent at all — see the plan's §1/§2).
 * This is also the one place that decides "did the payment succeed" — it
 * always asks the backend, never trusts what a redirect claimed about itself.
 */
export function usePaymentStatusPolling() {
  const [isPolling, setIsPolling] = useState(false);
  const cancelledRef = useRef(false);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    setIsPolling(false);
  }, []);

  const start = useCallback(
    async ({
      paymentId,
      dbPtr,
      authToken,
    }: PollArgs): Promise<PollingOutcome> => {
      cancelledRef.current = false;
      setIsPolling(true);
      const deadline = Date.now() + MAX_WINDOW_MS;
      let delay = INITIAL_DELAY_MS;

      try {
        while (Date.now() < deadline) {
          if (cancelledRef.current) return { success: false, timedOut: false };

          try {
            const response = await fetch(
              `${env.VITE_API_BASE_URL}/bookings/payment-status?payment_id=${paymentId}`,
              {
                headers: {
                  db_ptr: dbPtr,
                  Authorization: `Bearer ${authToken}`,
                },
              }
            );

            const result = await response.json();

            if (result.success) {
              return { success: true, appointmentId: result.data };
            }
          } catch (error) {
            // A single failed poll doesn't end the window — the network blip
            // itself is retried by the next tick, same as before.
            reportError(error, { paymentId, phase: 'status-poll' });
          }

          await new Promise((resolve) => setTimeout(resolve, delay));
          delay = Math.min(delay * BACKOFF_FACTOR, MAX_DELAY_MS);
        }

        return { success: false, timedOut: true };
      } finally {
        setIsPolling(false);
      }
    },
    []
  );

  return { start, stop, isPolling };
}
